import React, { useState, useMemo } from 'react';
import { Button, Space, Tag, Tooltip, message, Modal, Select, Popconfirm } from 'antd';
import {
  EyeOutlined,
  UserAddOutlined,
  PhoneOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Lead, batchClaimLeads, batchReleaseLeads, batchAssignLeads, batchDeleteLeads } from '../../../services/lead';
import { getAssignableUsers, UserAssignee } from '../../../services/user';
import { getCurrentUser } from '../../../services/auth';
import { UserRole } from '../../../constants/user';
import OutboundCallButton from '../../../components/OutboundCallButton';
import ResizableTable from '../../../components/ResizableTable';
import TableColumnSettings from '../../../components/TableColumnSettings';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { publicPoolDefaultColumns, myLeadsDefaultColumns } from './leadColumns.config';

dayjs.extend(utc);
dayjs.extend(timezone);

// 格式化距离上次跟进的时间
const formatLastFollowTime = (lead: Lead): string => {
  const timeStr = lead.last_follow_time;
  
  if (!timeStr) return '-';
  
  const now = dayjs();
  const time = dayjs(timeStr);
  const days = now.diff(time, 'day');
  
  if (days === 0) return '今天';
  if (days === 1) return '1天前';
  if (days === 2) return '2天前';
  if (days === 3) return '3天前';
  if (days < 7) return `${days}天前`;
  if (days < 14) return '1周前';
  if (days < 30) return '半个月前';
  if (days < 60) return '1个月前';
  if (days < 90) return '2个月前';
  if (days < 180) return '3个月前';
  if (days < 365) return '半年前';
  
  const years = Math.floor(days / 365);
  return `${years}年前`;
};

interface LeadListViewEnhancedProps {
  leads: Lead[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  onView: (lead: Lead) => void;
  onEdit?: (lead: Lead) => void;
  onDelete?: (leadId: number) => void;
  onClaim?: (lead: Lead) => void;
  onAbandon?: (lead: Lead) => void;
  onRefresh?: () => void;
  showActions: 'public-pool' | 'my-leads' | 'all-leads';
  sortField?: string;
  sortOrder?: 'ascend' | 'descend';
  onSortChange?: (field: string | undefined, order: 'ascend' | 'descend' | undefined) => void;
}

const LeadListViewEnhanced: React.FC<LeadListViewEnhancedProps> = ({
  leads,
  loading,
  total,
  page,
  pageSize,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onClaim,
  onAbandon,
  onRefresh,
  showActions,
  sortField,
  sortOrder,
  onSortChange,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState<UserAssignee[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>();
  const [currentUserRole, setCurrentUserRole] = useState<UserRole | null>(null);
  const [callModalVisible, setCallModalVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);

  // 列配置管理
  const defaultColumns = showActions === 'public-pool' ? publicPoolDefaultColumns : myLeadsDefaultColumns;
  const {
    columns: columnConfigs,
    updateColumnWidth,
    updateColumnOrder,
    updateColumnVisibility,
    resetSettings,
  } = useTableColumns(showActions, defaultColumns);

  // Load current user role
  React.useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const user = await getCurrentUser();
        setCurrentUserRole(user.role);
      } catch (error) {
        console.error('Failed to load current user', error);
      }
    };
    loadCurrentUser();
  }, []);

  // 构建列渲染映射
  const columnRenderMap: Record<string, any> = useMemo(() => ({
    index: {
      render: (_text: any, _record: any, index: number) => (page - 1) * pageSize + index + 1,
    },
    contact_name: {
      ellipsis: { showTitle: false },
      render: (name: string, record: Lead) => (
        <Tooltip title={name} placement="topLeft">
          <Button
            type="link"
            onClick={() => onView(record)}
            style={{ padding: 0, height: 'auto', fontWeight: 'bold', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', textAlign: 'left' }}
          >
            {name}
          </Button>
        </Tooltip>
      ),
    },
    contact: {
      render: (_: any, record: Lead) => (
        <div
          data-phone-cell="true"
          style={{ cursor: showActions === 'my-leads' && record.phone ? 'pointer' : 'default' }}
          onClick={(e) => {
            if (showActions === 'my-leads' && record.phone) {
              e.stopPropagation();
              setSelectedLead(record);
              setCallModalVisible(true);
            }
          }}
        >
          <Space direction="vertical" size={2}>
            {record.phone && (
              <Tooltip title={(record as any).is_blacklisted ? `黑名单：${(record as any).blacklist_remark || '无备注'}` : ''}>
                <span style={{ color: (record as any).is_blacklisted ? '#ff4d4f' : (showActions === 'my-leads' ? '#1890ff' : 'inherit') }}>
                  <PhoneOutlined /> {record.phone}
                </span>
              </Tooltip>
            )}
            {(record.province || record.city || record.district) && (
              <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
                {record.province || ''}{record.city || ''}{record.district || ''}
              </span>
            )}
          </Space>
        </div>
      ),
    },
    follow_status: {
      render: (followStatus: string) => {
        if (!followStatus) return <Tag color="#d9d9d9">未联系</Tag>;
        const colorMap: Record<string, string> = {
          '未联系': '#d9d9d9', '未接通': '#faad14', '无效': '#ff4d4f', '空号': '#ff4d4f',
          '稍后联系': '#1890ff', '重复': '#ff7a45', '客诉': '#f5222d', '已加微信': '#52c41a',
          '已加企微': '#13c2c2', '邀约到所': '#722ed1', '已成交': '#389e0d',
        };
        return <Tag color={colorMap[followStatus] || 'default'}>{followStatus}</Tag>;
      },
    },
    last_follow: {
      sorter: true,
      sortOrder: sortField === 'last_follow_time' ? sortOrder : null,
      render: (_: any, record: Lead) => {
        const timeText = formatLastFollowTime(record);
        const getColor = () => {
          if (timeText === '今天') return '#52c41a';
          if (timeText.includes('天前') && !timeText.includes('周') && !timeText.includes('月')) {
            const days = parseInt(timeText);
            if (days <= 3) return '#1890ff';
            if (days <= 7) return '#faad14';
          }
          if (timeText.includes('周') || timeText.includes('半个月')) return '#ff7a45';
          return '#ff4d4f';
        };
        return <span style={{ color: getColor() }}>{timeText}</span>;
      },
    },
    next_follow_time: {
      sorter: true,
      sortOrder: sortField === 'next_follow_time' ? sortOrder : null,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
    },
    case_description: {
      ellipsis: { showTitle: false },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text || '-'}>
          <span>{text || '-'}</span>
        </Tooltip>
      ),
    },
    urgency_level: {
      render: (urgencyLevel: string) => {
        const level = urgencyLevel || 'D类';
        const colorMap: Record<string, string> = {
          'A类': '#ff4d4f', 'B类': '#faad14', 'C类': '#52c41a', 'D类': '#1890ff',
        };
        return <Tag color={colorMap[level] || 'default'}>{level}</Tag>;
      },
    },
    assignee: {
      render: (_: any, record: Lead) => {
        // 有负责人就显示，没有就显示 -
        return record.assignee_name || '-';
      },
    },
    person: {
      render: (_: any, record: Lead) =>
        showActions === 'my-leads' ? (record.assignee_name || '-') : (record.creator_name || '-'),
    },
    collaborators: {
      render: (_: any, record: Lead) => {
        if (!record.collaborators || record.collaborators.length === 0) return '-';
        if (record.collaborators.length === 1) return record.collaborators[0];
        return (
          <Tooltip title={record.collaborators.join('、')}>
            <span>{record.collaborators.length}人</span>
          </Tooltip>
        );
      },
    },
    assigned_at: {
      sorter: showActions === 'my-leads',
      sortOrder: sortField === 'assigned_at' ? sortOrder : null,
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
    },
    created_at: {
      sorter: true,
      sortOrder: sortField === 'created_at' ? sortOrder : null,
      render: (date: string) => date ? dayjs.utc(date).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm') : '-',
    },
    action: {
      render: (_: any, record: Lead) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => onView(record)}>查看</Button>
          {showActions === 'public-pool' && onClaim && !record.assigned_to && (
            <Button type="link" size="small" icon={<UserAddOutlined />} onClick={() => onClaim(record)}>认领</Button>
          )}
          {showActions === 'my-leads' && onAbandon && (
            <Popconfirm title="确定要放弃此线索吗？" description="线索将返回公海池" onConfirm={() => onAbandon(record)} okText="确定" cancelText="取消">
              <Button type="link" size="small" danger>放弃</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  }), [page, pageSize, showActions, onView, onClaim, onAbandon, sortField, sortOrder]);

  // 合并列配置和渲染逻辑
  const columns: ColumnsType<Lead> = useMemo(() => {
    return columnConfigs
      .filter(col => col.visible)
      .map(col => ({
        ...col,
        ...(columnRenderMap[col.key] || {}),
      }));
  }, [columnConfigs, columnRenderMap]);

  // Batch operations
  const handleBatchClaim = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要认领的线索');
      return;
    }
    setBatchLoading(true);
    try {
      const result = await batchClaimLeads(selectedRowKeys as number[]);
      if (result.success_count > 0) {
        message.success(`成功认领 ${result.success_count} 条线索`);
        setSelectedRowKeys([]);
        onRefresh?.();
      }
      if (result.failed_count > 0) {
        message.warning(`${result.failed_count} 条线索认领失败`);
      }
    } catch (error) {
      message.error('批量认领失败');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleBatchRelease = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要放弃的线索');
      return;
    }
    Modal.confirm({
      title: '确认放弃',
      content: `确定要放弃选中的 ${selectedRowKeys.length} 条线索吗？线索将返回公海池。`,
      onOk: async () => {
        setBatchLoading(true);
        try {
          const result = await batchReleaseLeads(selectedRowKeys as number[]);
          if (result.success_count > 0) {
            message.success(`成功放弃 ${result.success_count} 条线索`);
            setSelectedRowKeys([]);
            onRefresh?.();
          }
          if (result.failed_count > 0) {
            message.warning(`${result.failed_count} 条线索放弃失败`);
          }
        } catch (error) {
          message.error('批量放弃失败');
        } finally {
          setBatchLoading(false);
        }
      },
    });
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的线索');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条线索吗？`,
      okText: '确认删除',
      okType: 'danger',
      onOk: async () => {
        setBatchLoading(true);
        try {
          const result = await batchDeleteLeads(selectedRowKeys as number[]);
          if (result.success_count > 0) {
            message.success(`成功删除 ${result.success_count} 条线索`);
            setSelectedRowKeys([]);
            onRefresh?.();
          }
          if (result.failed_count > 0) {
            message.warning(`${result.failed_count} 条线索删除失败`);
          }
        } catch (error) {
          message.error('批量删除失败');
        } finally {
          setBatchLoading(false);
        }
      },
    });
  };

  const handleBatchAssign = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要分配的线索');
      return;
    }
    try {
      const users = await getAssignableUsers();
      setAssignableUsers(users);
      setAssignModalVisible(true);
    } catch (error) {
      message.error('加载用户列表失败');
    }
  };

  const confirmBatchAssign = async () => {
    if (!selectedUserId) {
      message.warning('请选择分配对象');
      return;
    }
    setBatchLoading(true);
    try {
      const result = await batchAssignLeads(selectedRowKeys as number[], selectedUserId);
      message.success(`成功分配 ${result.count} 条线索`);
      setSelectedRowKeys([]);
      setAssignModalVisible(false);
      setSelectedUserId(undefined);
      onRefresh?.();
    } catch (error) {
      message.error('批量分配失败');
    } finally {
      setBatchLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: Key[]) => setSelectedRowKeys(selectedKeys),
  };

  return (
    <>
      {/* Batch Actions Bar */}
      {selectedRowKeys.length > 0 && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#e6f7ff', borderRadius: 4 }}>
          <Space>
            <span>已选择 {selectedRowKeys.length} 项</span>
            {showActions === 'public-pool' && currentUserRole === UserRole.ADMIN && (
              <Button type="primary" icon={<UserSwitchOutlined />} onClick={handleBatchAssign} loading={batchLoading}>
                批量分配
              </Button>
            )}
            {showActions === 'public-pool' && currentUserRole !== UserRole.ADMIN && (
              <Button type="primary" icon={<UserAddOutlined />} onClick={handleBatchClaim} loading={batchLoading}>
                批量认领
              </Button>
            )}
            {showActions === 'my-leads' && (
              <>
                <Button danger onClick={handleBatchRelease} loading={batchLoading}>批量放弃</Button>
                {currentUserRole === UserRole.ADMIN && (
                  <Button danger onClick={handleBatchDelete} loading={batchLoading}>批量删除</Button>
                )}
              </>
            )}
            {showActions === 'public-pool' && currentUserRole === UserRole.ADMIN && (
              <Button danger onClick={handleBatchDelete} loading={batchLoading}>批量删除</Button>
            )}
            <Button onClick={() => setSelectedRowKeys([])}>取消选择</Button>
          </Space>
        </div>
      )}

      {/* Column Settings Button */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <TableColumnSettings
          columns={columnConfigs}
          onVisibilityChange={updateColumnVisibility}
          onOrderChange={updateColumnOrder}
          onReset={resetSettings}
        />
      </div>

      <ResizableTable
        columns={columns}
        dataSource={leads}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        scroll={{ x: 1200 }}
        onColumnResize={updateColumnWidth}
        onChange={(pagination, filters, sorter: any) => {
          if (onSortChange) {
            if (sorter.order) {
              onSortChange(sorter.field, sorter.order);
            } else {
              onSortChange(undefined, undefined);
            }
          }
        }}
        onRow={(record) => ({
          onClick: (e) => {
            const target = e.target as HTMLElement;
            if (showActions === 'public-pool') {
              if (!target.closest('button') && !target.closest('.ant-checkbox-wrapper')) {
                onView(record);
              }
            } else if (showActions === 'my-leads') {
              if (!target.closest('button') && !target.closest('.ant-checkbox-wrapper') && !target.closest('[data-phone-cell]')) {
                onView(record);
              }
            }
          },
          style: { cursor: 'pointer' }
        })}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: onPageChange,
        }}
      />

      {/* Batch Assign Modal */}
      <Modal
        title="批量分配线索"
        open={assignModalVisible}
        onOk={confirmBatchAssign}
        onCancel={() => {
          setAssignModalVisible(false);
          setSelectedUserId(undefined);
        }}
        confirmLoading={batchLoading}
      >
        <p>将选中的 {selectedRowKeys.length} 条线索分配给：</p>
        <Select
          style={{ width: '100%' }}
          placeholder="请选择负责人"
          value={selectedUserId}
          onChange={setSelectedUserId}
          showSearch
          optionFilterProp="children"
        >
          {assignableUsers.map((user) => (
            <Select.Option key={user.id} value={user.id}>
              {user.real_name}
            </Select.Option>
          ))}
        </Select>
      </Modal>

      {/* Call Component */}
      {selectedLead && selectedLead.phone && callModalVisible && (
        <OutboundCallButton
          leadId={selectedLead.id}
          leadName={selectedLead.contact_name || ''}
          phone={selectedLead.phone}
          autoOpen={true}
          hideButton={true}
          onCancel={() => {
            setCallModalVisible(false);
            setSelectedLead(null);
          }}
          onCallComplete={() => {
            setCallModalVisible(false);
            setSelectedLead(null);
            onRefresh?.();
          }}
          onOpenLeadDetail={(leadId) => {
            setCallModalVisible(false);
            setSelectedLead(null);
            const lead = leads.find(l => l.id === leadId);
            if (lead) {
              onView(lead);
            } else {
              onView({ id: leadId } as Lead);
            }
          }}
        />
      )}
    </>
  );
};

export default LeadListViewEnhanced;
